import CoverageNotice from "./CoverageNotice";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, WhatsappLogo, Images, VideoCamera, Users, MapPin } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { DlMediaViewer, Thumb, fmtET } from "@/components/dailylogs/DlMediaViewer";
import { CrewCard, SiteCard, bySite } from "@/components/dailylogs/CrewBoard";
import { mediaSrc } from "@/components/service/serviceApi";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const nyNow = () => new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const monday = (d) => addDays(d, -((d.getDay() + 6) % 7));
export function rangeFor(k) {
  const t = nyNow();
  if (k === "today") return [iso(t), iso(t)];
  if (k === "yesterday") return [iso(addDays(t, -1)), iso(addDays(t, -1))];
  if (k === "this_week") return [iso(monday(t)), iso(t)];
  if (k === "last_week") { const m = addDays(monday(t), -7); return [iso(m), iso(addDays(m, 6))]; }
  return null;
}
const PILLS = [["today", "Today"], ["yesterday", "Yesterday"], ["date", "Date"], ["range", "Date range"], ["this_week", "This week"], ["last_week", "Last week"]];

function Connection({ c }) {
  const ok = c.status === "connected";
  return (
    <div className={`flex flex-wrap items-center gap-2 text-[11px] rounded-lg px-3 py-1.5 border ${ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700" : "border-amber-500/40 bg-amber-500/10 text-[#e9d99a]"}`} data-testid="dl-connection">
      <WhatsappLogo size={14} weight="fill" /><b>{ok ? `Connected · ${c.push_name || ""}` : c.status === "waiting_qr" || c.status === "session_expired" ? "Not linked — open WhatsApp Settings and scan the QR code" : `WhatsApp ${c.status.replace("_", " ")} — saved logs below are still available. Use Reconnect WhatsApp (top right).`}</b>
      <span className="text-slate-500">Groups: {c.groups.map((g) => `${g.name}${g.last_capture_at ? ` (last sync ${fmtET(g.last_capture_at)})` : ""}`).join(", ") || "none selected"}</span>
    </div>
  );
}

function Kpis({ k }) {
  const stat = (I, l, v, id) => <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px]" data-testid={`dl-kpi-${id}`}><I size={13} className="text-[#327de3]" /><span className="text-slate-500">{l}</span><b className="text-slate-900">{v}</b></div>;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {stat(Users, "Crews", k.crew_count, "crews")}{stat(MapPin, "Sites", k.sites, "sites")}{stat(Images, "Photos", k.photos, "photos")}{stat(VideoCamera, "Videos", k.videos, "videos")}
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white text-[11px] overflow-hidden" data-testid="dl-kpi-total"><span className="px-3 py-1 text-slate-500">Assigned <b className="text-slate-900">{k.assigned}</b></span><span className="px-3 py-1 bg-emerald-500/10 text-emerald-700">Completed <b>{k.completed}</b></span>{k.review > 0 && <span className="px-3 py-1 bg-amber-500/10 text-[#ff8a3d]">Review {k.review}</span>}</div>
        {k.missing_media > 0 && <span className="inline-flex items-center rounded-full bg-rose-500/10 text-rose-600 px-3 py-1 text-[11px]" data-testid="dl-kpi-missing">{k.missing_media} media not archived</span>}
      </div>
      <div className="flex md:grid md:grid-cols-3 xl:grid-cols-6 gap-2 overflow-x-auto pb-1 snap-x" data-testid="dl-crew-pills">
        {k.crews.map((c) => (
          <div key={c.crew} className="min-w-[200px] md:min-w-0 snap-start rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 space-y-1" data-testid={`dl-crew-pill-${c.crew}`}>
            <div className="text-[15px] font-bold text-slate-900 leading-tight break-words">{c.crew}</div>
            <div className="text-[12px] text-slate-500 flex flex-wrap gap-x-1.5"><span className="text-[#efe4b0] font-semibold">Assigned: {c.assigned}</span> <span className="text-slate-300">|</span> <span className="text-emerald-500 font-semibold">Completed: {c.completed}</span></div>
            {c.review > 0 && <div className="text-[11px] text-[#ff8a3d] font-semibold">Needs review: {c.review}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Search({ onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return undefined; }
    const t = setTimeout(() => api.get("/dailylogs/search", { params: { q } }).then(({ data }) => setRes(data)).catch(() => setRes([])), 350);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative w-full max-w-md">
      <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: avi unit 40 cleanup, tony hector completed yesterday…" className="h-8 pl-8 text-xs" data-testid="dl-search" />
      {res && <div className="absolute z-30 mt-1 w-full max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="dl-search-results">
        {res.length === 0 && <div className="p-3 text-xs text-slate-500">No matches</div>}
        {res.map((r) => (
          <button key={`${r.type}${r.id}`} onClick={() => { onPick(r); setRes(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50" data-testid={`dl-search-hit-${r.id}`}>
            {r.thumb_url ? <img src={mediaSrc(r.thumb_url)} alt="" className="w-8 h-8 rounded object-cover" /> : <span className="w-8 text-[9px] uppercase font-bold text-slate-400">{r.status?.replace("_", " ") || r.type}</span>}
            <span className="min-w-0"><span className="block text-xs font-semibold text-slate-800 truncate">{r.title}</span><span className="block text-[10px] text-slate-500 truncate">{r.date} · {r.subtitle}</span></span>
          </button>
        ))}
      </div>}
    </div>
  );
}

export function RangePills({ mode, pick, from, to, setFrom, setTo }) {
  return (
    <>
      <div className="inline-flex flex-wrap bg-slate-100 rounded-full p-1 gap-1">
        {PILLS.map(([k, l]) => <button key={k} onClick={() => pick(k)} data-testid={`dl-pill-${k}`} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${mode === k ? "bg-[#327de3] text-white shadow" : "text-slate-600 hover:bg-white"}`}>{l}</button>)}
      </div>
      {(mode === "date" || mode === "range") && <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); if (mode === "date") setTo(e.target.value); }} className="h-8 w-36 text-xs" data-testid="dl-range-from" />}
      {mode === "range" && <><span className="text-xs text-slate-400">to</span><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="dl-range-to" /></>}
    </>
  );
}

export function useRange(initial = "today") {
  const [mode, setMode] = useState(initial);
  const [[from, to], setRange] = useState(rangeFor(initial));
  const setFrom = (v) => setRange(([, t]) => [v, t]);
  const setTo = (v) => setRange(([f]) => [f, v]);
  const pick = (k) => { setMode(k); const r = rangeFor(k); if (r) setRange(r); else if (k === "date") setRange(([f]) => [f, f]); };
  return { mode, setMode, from, to, setFrom, setTo, setRange, pick };
}

function Toolbar({ mode, pick, from, to, setFrom, setTo, groups, gid, setGid, view, setView }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="dl-range-pills">
      <RangePills {...{ mode, pick, from, to, setFrom, setTo }} />
      {groups.length > 1 && <select value={gid} onChange={(e) => setGid(e.target.value)} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="dl-group-filter"><option value="">All selected groups</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>}
      <div className="inline-flex bg-slate-100 rounded-full p-1 gap-1 ml-auto">{[["crew", "By crew"], ["site", "By site"]].map(([k, l]) => <button key={k} onClick={() => setView(k)} data-testid={`dl-view-${k}`} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${view === k ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}>{l}</button>)}</div>
    </div>
  );
}

export function OverviewBoard() {
  const { mode, setMode, from, to, setFrom, setTo, setRange, pick } = useRange("today");
  const [gid, setGid] = useState("");
  const [view, setView] = useState("crew");
  const [b, setB] = useState(null);
  const [hl, setHl] = useState(null);
  const [viewer, setViewer] = useState(null);
  const load = useCallback(() => {
    if (!from || !to || to < from) return;
    api.get("/dailylogs/board", { params: { date_from: from, date_to: to, group_id: gid || undefined } }).then(({ data }) => setB(data)).catch(() => setB(null));
  }, [from, to, gid]);
  useEffect(() => {
    load(); const t = setInterval(load, 30000);
    window.addEventListener("dl-synced", load);
    return () => { clearInterval(t); window.removeEventListener("dl-synced", load); };
  }, [load]);
  useEffect(() => { if (hl) { const t = setTimeout(() => document.getElementById(`dl-item-${hl}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 600); return () => clearTimeout(t); } return undefined; }, [hl, b]);
  const onPick = (r) => { setMode("date"); setRange([r.date, r.date]); setHl(r.id); };
  const onMedia = (items, m) => setViewer({ items, index: items.findIndex((x) => x.id === m.id) });
  const ai = b?.ai;
  return (
    <div className="space-y-3" data-testid="dl-overview">
      {b && <Connection c={b.connection} />}
      <Toolbar {...{ mode, pick, from, to, setFrom, setTo, groups: b?.connection.groups || [], gid, setGid, view, setView }} />
      <CoverageNotice from={from} to={to} />
      <div className="flex flex-wrap items-center gap-3">
        <Search onPick={onPick} />
        <span className="text-xs text-slate-500" data-testid="dl-range-label">{from === to ? from : `${from} → ${to}`}</span>
        {b?.analysis && (
          <span className="text-[11px] text-slate-400" data-testid="dl-analysis-status">
            {b.analysis.text_messages} text message{b.analysis.text_messages === 1 ? "" : "s"} · {b.analysis.pending ? `${b.analysis.pending} day(s) waiting for analysis` : b.analysis.last_analyzed_at ? `text analyzed ${fmtET(b.analysis.last_analyzed_at)}` : "not analyzed yet"}
            {b.analysis.errors.length > 0 && <span className="text-rose-500" title={b.analysis.errors.map((e) => `${e.date}: ${e.error}`).join("\n")} data-testid="dl-analysis-errors"> · analysis failed for {b.analysis.errors.map((e) => e.date).join(", ")} (hover for details)</span>}
          </span>
        )}
        {ai && (
          <span className="text-[11px] inline-flex flex-wrap gap-x-3" data-testid="dl-ai-status">
            <span className={ai.days_pending > 0 || (ai.running && !ai.media_pending) ? "text-[#327de3]" : "text-slate-400"} data-testid="dl-text-lane-status">
              Text analysis: {ai.days_pending > 0 ? `${ai.days_pending} day(s) queued${ai.step ? ` — ${ai.step}` : ""}` : `up to date${ai.last_run ? ` · ${fmtET(ai.last_run)}` : ""}`}
            </span>
            {ai.media_pending > 0 && <span className="text-slate-400" data-testid="dl-media-lane-status">Photo AI (optional): {ai.media_pending} pending — does not affect task cards</span>}
          </span>
        )}
      </div>
      {b && <Kpis k={b.kpis} />}
      {b && b.crews.length === 0 && (b.analysis?.text_messages > 0 && (b.analysis.pending > 0 || b.analysis.errors.length > 0) ? (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-4 px-4 text-center" data-testid="dl-board-pending">
          {b.analysis.text_messages} text message{b.analysis.text_messages === 1 ? "" : "s"} in this period {b.analysis.errors.length ? "could not be analyzed yet (retrying automatically)" : "are being analyzed"} — task cards appear here as each day finishes.
        </div>
      ) : <div className="text-sm text-slate-500 py-10 text-center" data-testid="dl-board-empty">No assigned or reported work for this period.</div>)}
      <div className="columns-1 md:columns-2 2xl:columns-3 gap-3" data-testid="dl-board">
        {view === "crew" ? b?.crews.map((c) => <CrewCard key={c.crew} c={c} hl={hl} onMedia={onMedia} onSaved={load} />)
          : b && bySite(b.crews).map((s) => <SiteCard key={s.key} s={s} hl={hl} onMedia={onMedia} onSaved={load} />)}
      </div>
      {b?.other_media.length > 0 && <div className="rounded-xl border border-dashed border-slate-300 p-2.5" data-testid="dl-other-media">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1.5">Other media · crew or location unconfirmed · {b.other_media.length}</div>
        <div className="flex flex-wrap gap-1">{b.other_media.map((m) => <Thumb key={m.id} m={m} className="w-11 h-11" onClick={() => onMedia(b.other_media, m)} />)}</div>
      </div>}
      {viewer && <DlMediaViewer items={viewer.items} index={viewer.index} onClose={() => setViewer(null)} onChanged={load} />}
    </div>
  );
}
